import React, { useMemo, useCallback, useRef } from 'react';
import DataGrid from '@dataGrid';
import StatusBar from '@dataGrid/components/StatusBar';
import { withStyles } from '@material-ui/core/styles';
import appConstants from '@appConstants';
import GridConfig from '../../../../../modules/Dispatch/RouteExecution/Drivers/Grid/GridConfig';
import { applySelectDriversGridOverrides } from './config/selectDriversGridConfig';
import { useSelectDriversContext } from '../SelectDriversContext';
import styles from '../styles';

export interface RouteRow {
  driverId: string;
  routeId?: string;
  [key: string]: unknown;
}

export interface DriversGridSectionProps {
  classes?: Record<string, string>;
}

const DriversGridSection: React.FC<DriversGridSectionProps> = ({ classes }) => {
  const {
    routes,
    selectedDriverIds,
    onSelectionChange,
    userData,
    configJSON,
    quickFilterText,
  } = useSelectDriversContext();

  const userDataTyped = userData as { userProfile?: { terminal?: { offset?: number }; columnPref?: unknown } };
  const configJSONTyped = configJSON as { enableColumnShortForm?: boolean } | undefined;
  const gridApiRef = useRef<{ forEachNode: (cb: (node: { setSelected: (v: boolean) => void; data?: RouteRow }) => void) => void; getSelectedRows: () => RouteRow[] } | null>(null);

  const featureFlagsForGrid = useMemo(
    () => ({
      enableColumnShortForm: configJSONTyped?.enableColumnShortForm,
    }),
    [configJSONTyped?.enableColumnShortForm]
  );

  // Same driver grid but separate preferences key so column prefs are saved/loaded per grid
  const baseConfig = useMemo(
    () =>
      GridConfig.getConfiguration({
        userProfile: userDataTyped?.userProfile,
        featureFlags: featureFlagsForGrid,
        gridTitle: appConstants.gridNames.PickupOptimizationSelectDriversGrid,
      }),
    [userDataTyped?.userProfile, featureFlagsForGrid]
  );

  const gridConfig = useMemo(
    () => applySelectDriversGridOverrides(baseConfig),
    [baseConfig]
  );

  const terminalOffset = userDataTyped?.userProfile?.terminal?.offset ?? 0;

  const getDriverRoutes = useCallback((driverId: string) => {
    return routes.filter((r) => r.driverId === driverId);
  }, [routes]);

  const gridContext = useMemo(
    () => ({ terminalOffset, getDriverRoutes }),
    [terminalOffset, getDriverRoutes]
  );

  const onGridReady = useCallback(
    ({ api }: { api: typeof gridApiRef.current }) => {
      gridApiRef.current = api;
      if (api && routes.length > 0) {
        api.forEachNode((node) => {
          if (node.data) node.setSelected(true);
        });
        const allIds = routes.map((r) => r.driverId).filter(Boolean);
        onSelectionChange(allIds);
      }
    },
    [routes, onSelectionChange]
  );

  const onSelectionChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const selected = gridApiRef.current
      .getSelectedRows()
      .map((r) => r.driverId)
      .filter(Boolean);
    onSelectionChange(selected);
  }, [onSelectionChange]);

  const constructStatusBar = useCallback(
    (filteredData: RouteRow[]) => [
      { label: `${filteredData.length} Total Drivers`, filterCondition: '' },
      { label: `${selectedDriverIds.length} Selected Drivers`, filterCondition: '' },
    ],
    [selectedDriverIds.length]
  );

  return (
    <div className={classes?.bottomGridContainer}>
      <div className={classes?.dataGrid}>
        <DataGrid
          defaultColDef={gridConfig.defaultColDef}
          columnDefs={gridConfig.columns}
          columnTypes={gridConfig.columnTypes}
          config={gridConfig}
          context={gridContext}
          rowSelection="multiple"
          rowDeselection={true}
          rowData={routes}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          getRowId={(params) => params.data.driverId}
          panelLabel={(filteredData) => (
            <StatusBar data={constructStatusBar(filteredData as RouteRow[])} />
          )}
          suppressRowClickSelection
          quickFilterText={quickFilterText}
        />
      </div>
    </div>
  );
};

export default withStyles(styles)(DriversGridSection);
